/**
 * vision.js — Line-of-sight visibility for fog of war
 *
 * Ray-cast from token centre to sample points in each candidate cell.
 * A cell is visible if ANY of its 5 samples has an unobstructed sightline.
 */

export const VISION_RADIUS = 10; // cells (50 ft)

/** Build raw wall segment list [[x1,y1,x2,y2], …] for ray intersection tests. */
import { flattenWalls } from './pathfinding';

export function buildWallSegs(walls) {
  const segs = [];
  if (!walls) return segs;
  for (const wall of flattenWalls(walls)) {
    const pts = wall.pts;
    if (!pts || pts.length < 2) continue;
    for (let i = 0; i < pts.length - 1; i++)
      segs.push([pts[i].x, pts[i].y, pts[i + 1].x, pts[i + 1].y]);
    if (wall.closed && pts.length > 2)
      segs.push([pts[pts.length - 1].x, pts[pts.length - 1].y, pts[0].x, pts[0].y]);
  }
  return segs;
}

/**
 * Does the ray from (x1,y1)→(x2,y2) cross any wall segment?
 * t (ray param) is open (0,1) — don't block at the origin or the target cell.
 * u (wall param) is closed [0,1] — block at wall endpoints so rays can't slip
 * through vertices where two wall segments meet.
 */
function rayHitsWall(x1, y1, x2, y2, wallSegs) {
  const EPS = 1e-6;
  const rdx = x2 - x1, rdy = y2 - y1;
  for (const [ax, ay, bx, by] of wallSegs) {
    const wdx = bx - ax, wdy = by - ay;
    const denom = rdx * wdy - rdy * wdx;
    if (Math.abs(denom) < EPS) continue; // parallel
    const t = ((ax - x1) * wdy - (ay - y1) * wdx) / denom;
    if (t <= EPS || t >= 1 - EPS) continue; // miss: behind origin or past target
    const u = ((ax - x1) * rdy - (ay - y1) * rdx) / denom;
    if (u >= -EPS && u <= 1 + EPS) return true; // hit anywhere along the wall
  }
  return false;
}

/**
 * Build vision-blocking segments from doors.
 * Closed/locked doors block player vision. Open doors do not.
 * Unrevealed secret doors always block (they look like walls).
 * DM sees through everything.
 */
export function buildDoorVisionSegs(doors, isDm) {
  if (isDm || !doors) return [];
  const segs = [];
  for (const door of doors) {
    // Open doors don't block vision
    if (door.state === 'open') continue;
    // Unrevealed secrets always block
    // Closed/locked regular doors also block
    const [a, b] = door.pts;
    segs.push([a.x, a.y, b.x, b.y]);
  }
  return segs;
}

/**
 * Returns every [x,y] cell within visionRadius that has an unobstructed line
 * from the token centre to any of five sample points in the target cell.
 * @param {number} [visionRadius] — override; defaults to VISION_RADIUS (10)
 */
export function computeVisibleCells(tokenX, tokenY, tokenSize, wallSegs, cells, gridW, gridH, visionRadius) {
  const radius = visionRadius ?? VISION_RADIUS;
  const cx = tokenX + tokenSize / 2;
  const cy = tokenY + tokenSize / 2;
  const visible = [];
  const M = 0.15; // inset margin for corner samples

  const x0 = Math.max(0, Math.floor(cx - radius - 1));
  const x1 = Math.min(gridW - 1, Math.ceil(cx + radius));
  const y0 = Math.max(0, Math.floor(cy - radius - 1));
  const y1 = Math.min(gridH - 1, Math.ceil(cy + radius));

  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      if (Math.hypot(x + 0.5 - cx, y + 0.5 - cy) > radius + 0.5) continue;
      const cell = cells[y * gridW + x];
      if (!cell || cell.terrain === 'empty') continue;
      const samples = [
        [x + 0.5, y + 0.5],
        [x + M, y + M],
        [x + 1 - M, y + M],
        [x + M, y + 1 - M],
        [x + 1 - M, y + 1 - M],
      ];
      if (samples.some(([sx, sy]) => !rayHitsWall(cx, cy, sx, sy, wallSegs))) {
        visible.push([x, y]);
      }
    }
  }
  return visible;
}
